import { useMemo } from 'react';
import { ScheduleRequestHistoryItem, Source } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { OrderBy } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/booking_submission.pb';
import { startCase } from 'lodash-es';
import { ScheduleQueries, ScheduleTypes } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { UsersQueries } from '@frontend/api-users';
import { getFullName } from '@frontend/name';

export type ExtendedScheduleRequestHistoryItem = ScheduleRequestHistoryItem & {
  appointmentTypeColor?: string;
  createdAt?: string;
  isAiRequest?: boolean;
};

export const useGetScheduleRequestHistoryData = (
  locationId: string,
  pageConfig: ScheduleTypes.ScheduleRequestHistoryPageConfig
) => {
  const { isScheduleV3FlagEnabled, isLoading: isLoadingFF } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isScheduleDefinitionsEnabled } = SchedulerV3.Helpers.useGetScheduleDefinitionsFeatureFlagDetails();

  const listScheduleRequestsQueryV2 = ScheduleQueries.useGetScheduleRequestsHistory(
    {
      limit: pageConfig.pageSize,
      offset: pageConfig.offset,
      locationIds: locationId ? [locationId] : [],
      orderBy: 'date_time',
    },
    {
      enabled: !isLoadingFF && !isScheduleV3FlagEnabled,
    },
    locationId
  );

  const listScheduleRequestsQueryV3 = SchedulerV3.Queries.useListBookingSubmissionsQuery({
    request: {
      locationsIds: locationId ? [locationId] : [],
      limit: pageConfig.pageSize,
      page: Math.floor(pageConfig.offset / pageConfig.pageSize) + 1,
      orderBy: OrderBy.REVIEWED_AT,
    },
    options: {
      enabled: !isLoadingFF && isScheduleV3FlagEnabled && !!locationId,
    },
  });

  const { data: appointmentTypes, isLoading: isLoadingAppointmentTypes } =
    SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
      selectedLocationId: locationId,
      selectedLocationIds: [locationId],
    });

  const { data: bookableAppointmentTypesData, isLoading: isLoadingBookableAppointmentTypesData } =
    SchedulerV3.Helpers.useGetServiceTypesV3Data({
      selectedLocationId: locationId,
      isEnabled: isScheduleDefinitionsEnabled,
    });

  const locationUsers = UsersQueries.useGetMultiLocationsUsers({
    locationIds: [locationId],

    params: { showChildren: false },
  }).flatMap(({ data }) => data || []);

  const data = useMemo<ExtendedScheduleRequestHistoryItem[]>(() => {
    if (isScheduleV3FlagEnabled) {
      if (isLoadingAppointmentTypes || isLoadingBookableAppointmentTypesData) {
        return [];
      }
      // convert to v2 format
      return (listScheduleRequestsQueryV3.data?.bookingSubmissions ?? []).map<ScheduleRequestHistoryItem>(
        (scheduleRequest) => {
          const appointmentType = appointmentTypes?.appointmentTypes.find(
            (type) => type.id === scheduleRequest.appointmentTypeId
          );

          const appointmentTypeGroup = bookableAppointmentTypesData?.appointmentTypeGroups.find((group) =>
            group.bookableAppointmentTypes?.some(
              (bookableApptType) => bookableApptType.appointmentTypeId === scheduleRequest.appointmentTypeId
            )
          );

          const bookableAppointmentType = appointmentTypeGroup?.bookableAppointmentTypes?.find(
            (bookableApptType) => bookableApptType.appointmentTypeId === scheduleRequest.appointmentTypeId
          );

          const reviewedByUser = locationUsers.find((user) => user.UserID === scheduleRequest.reviewedBy);

          return {
            locationId: scheduleRequest.locationId,
            createdAt: scheduleRequest.createdAt, // only available in v3
            reviewedAt: scheduleRequest.reviewedAt,
            reviewedBy: getFullName(reviewedByUser ?? {}),
            appointmentType: appointmentType?.displayName || appointmentType?.externalName || '',
            appointmentTypeColor: appointmentType?.details?.clr,
            appoinmentOutcome: startCase(scheduleRequest.reviewedStatus?.toLowerCase()),
            recipient: getFullName(scheduleRequest),
            // In v2, messageDateTime is the appointment date and time. In v3, it is correctly named appointmentDateTime.
            messageDatetime: scheduleRequest.appointmentDateTime,
            bookableAppointmentType: bookableAppointmentType?.displayName || '',
            isAiRequest: !!scheduleRequest.bookingDetails, // TODO: Need to update this when we have flag or details for V2 API
          };
        }
      );
    }

    return (listScheduleRequestsQueryV2.data?.items ?? []).map((scheduleRequest) => ({
      ...scheduleRequest,
      isAiRequest: scheduleRequest?.source === Source.AIR,
    }));
  }, [
    isScheduleV3FlagEnabled,
    listScheduleRequestsQueryV2.data,
    listScheduleRequestsQueryV3.data,
    appointmentTypes,
    isLoadingAppointmentTypes,
    isLoadingBookableAppointmentTypesData,
    bookableAppointmentTypesData,
    locationUsers,
  ]);

  const isLoading = isScheduleV3FlagEnabled
    ? listScheduleRequestsQueryV3.isFetching
    : listScheduleRequestsQueryV2.isFetching;

  const refetch = isScheduleV3FlagEnabled ? listScheduleRequestsQueryV3.refetch : listScheduleRequestsQueryV2.refetch;

  return { data, isLoading, refetch };
};
